<template>
  <v-container fluid class="wrapper pa-0 ma-0" :class="wrapperClassPerMode">
    <div v-if="loading" class="mt-5 d-flex flex-column align-center loading">
      <h5 class="text-h4 mb-3">{{ loadingStatus }}</h5>
      <v-progress-circular indeterminate></v-progress-circular>
    </div>

    <AnnotationControls
      :class="annotationControlsClass"
      v-if="showMenu && withAnnotationControls"
      ref="annotationControls"
      :slide="slide"
      :viewer="viewer"
      :observations="observations"
      @annotation:selected="currentSelectedAnnotation = $event"
      @annotation:stored="onAnnotationStored"
      @annotation:created="onAnnotationCreated"
      @annotation:hide-details="currentSelectedAnnotation = null"
      :float-mode="floatModeActive"
      @float-mode:changed="floatModeActive = $event"
      :currentSelectedRawAnnotation="currentSelectedRawAnnotation"
      @running-operations:changed="$emit('running-operations:changed', $event)"
      @next-slide="$emit('next-slide')"
      class="mt-4"
    ></AnnotationControls>
    <DiagnosisControls
      :class="diagnosisControlsClass"
      v-if="showMenu && withDiagnosisControls"
      ref="annotationControls"
      :slide="slide"
      :slides="slides"
      :viewer="viewer"
      :observations="observations"
      :annotations="annotations"
      @annotation:selected="currentSelectedAnnotation = $event"
      @annotation:stored="onAnnotationStored"
      @annotation:created="onAnnotationCreated"
      @annotation:hide-details="currentSelectedAnnotation = null"
      :float-mode="floatModeActive"
      @float-mode:changed="floatModeActive = $event"
      :currentSelectedRawAnnotation="currentSelectedRawAnnotation"
      @running-operations:changed="$emit('running-operations:changed', $event)"
      @next-slide="$emit('next-slide')"
      @previous-slide="$emit('previous-slide')"
      @go-to-slide="goToSlide"
      @finalize="finalizeDiagnosis"
      class="mt-4"
    ></DiagnosisControls>
    <AnnotationInstructions
      ref="annotationInstructions"
      :class="annotationInstructionsClass"
      :showInstructions="withAnnotationInstructions && floatModeActive"
      class="mt-4 col-sm-3"
    >
    </AnnotationInstructions>
    <visualization
      ref="dcm"
      style="display: none"
      class="fullscreen"
      :imaging-study="imagingStudy"
      v-if="imagingStudy && initialized"
      :float-mode="floatModeActive"
      @loading:rendered="onRenderingSlide"
      @loading:search="addLoading(statusSearching)"
      @loading:metadata="addLoading(statusLoadingMetadata)"
      hide-loader
    ></visualization>

    <ValidationControls
      ref="validationControls"
      class="validation-controls z-index-2"
      v-if="showMenu && validationMode"
      :slide="slide"
      :current-selected-annotation="currentSelectedRawAnnotation"
      :viewer="viewer"
      @annotation:selected="onAnnotationSelected"
    ></ValidationControls>

    <AnnotationDetails
      :class="annotationDetailsClass"
      v-if="withAnnotationDetails && currentSelectedAnnotation !== null"
      :slide="slide"
      :annotation="currentSelectedRawAnnotation"
      :validation-mode="validationMode"
    ></AnnotationDetails>
    <!-- <v-dialog max-width="600" v-model="dialogStart">
      <v-card>
        <v-card-title>{{ project(slide.project).project_name }}</v-card-title>
        <v-card-text>
          {{ project(slide.project).details }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogStart = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog> -->
  </v-container>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import {
  fetchResource,
  fetchResourceWithIdentifier,
  prepareImagingStudy
} from "@/api/fhir";
import Visualization from "@/components/slide/Visualization";
import { observationToAnnotation } from "@/utils/deeppath-utils";
import AnnotationDetails from "@/components/slide/AnnotationDetails";
import AnnotationInstructions from "@/components/slide/AnnotationInstructions";
import AnnotationControls from "@/components/slide/AnnotationControls";
import DiagnosisControls from "@/components/slide/DiagnosisControls";
import SelectControl from "@/viewer/SelectControl";
import Vue from "vue";
import ManipulationControl from "@/components/slide/controls/ManipulationControl";
import {
  FEATURE_OBSERVATION_ID,
  FEATURE_STATUS,
  FEATURE_STYLE
} from "@/constants";
import ValidationControls from "@/components/slide/ValidationControls";
import { getAnnotationsForSlide } from "@/api/craiyon";
import { FIND, FIND_BY_IDENTIFIER, LOAD } from "@/store/modules/fhir";
import { FIND as FIND_PROJECT } from "@/store/modules/project";
import { CURRENT_SLIDE, LIST } from "@/store/modules/annotation";

export default {
  props: {
    withAnnotationControls: { type: Boolean, default: false },
    withDiagnosisControls: { type: Boolean, default: false },
    withAnnotationInstructions: { type: Boolean, default: false },
    withAnnotationDetails: { type: Boolean, default: false },
    withAnnotations: { type: Boolean, default: false },
    withProjectDialog: { type: Boolean, default: false },
    withSelectAnnotation: { type: Boolean, default: false },
    floatMode: { type: Boolean, default: false },
    validationMode: { type: Boolean, default: false },
    annotationMode: { type: Boolean, default: true }
  },
  data() {
    return {
      internalWithAnnotationControls: false,
      internalWithDiagnosisControls: false,
      internalWithAnnotationInstructions: false,
      internalWithAnnotationDetails: false,
      internalWthAnnotations: false,
      internalWithProjectDialog: false,
      internalAnnotationMode: false,
      internalWithSelectAnnotation: false,
      imagingStudy: null,
      annotationClasses: [],
      showMenu: false,
      loadingStack: [],
      annotations: [],
      dialogStart: false,
      currentSelectedAnnotation: null,
      observations: [],
      floatModeActive: false,
      rawAnnotations: [],
      initialized: false,
    };
  },
  computed: {
    ...mapState({
      token: state => state.authentication.keycloak.token,
      tokenParsed: state => state.authentication.keycloak.tokenParsed
    }),
    ...mapGetters({
      status: FIND,
      findFhirResourceByIdentifier: FIND_BY_IDENTIFIER,
      project: FIND_PROJECT,
      slide: CURRENT_SLIDE,
      slides: LIST,
    }),
    statusSearching() {
      return this.$t("visualizator.loading.searching")
    },
    statusLoadingMetadata() {
      return this.$t("visualizator.loading.loadingMetadata")
    },
    statusLoadingAnnotations() {
      return this.$t("visualizator.loading.loadingAnnotations")
    },
    statusPreparing() {
      return this.$t("visualizator.loading.preparing")
    },
    statusLoadingFHIR() {
      return this.$t("visualizator.loading.loadingFHIR")
    },
    statusRendering() {
      return this.$t("visualizator.loading.rendering")
    },
    loading() {
      return this.loadingStack.length !== 0;
    },
    loadingStatus() {
      return this.loadingStack[this.loadingStack.length - 1];
    },
    viewer() {
      return this.$refs.dcm.viewer;
    },
    map() {
      return this.$refs.dcm.viewer.map;
    },
    annotationControlsClass() {
      if (this.floatMode) return ["floating", "floating-left"];

      return ["z-index-2"];
    },
    diagnosisControlsClass() {
      if (this.floatMode) return ["floating", "floating-left"];

      return ["z-index-2"];
    },
    annotationInstructionsClass() {
      if (this.floatMode) return ["floating", "floating-right"];

      return ["z-index-2"];
    },
    annotationDetailsClass() {
      if (this.floatMode) return ["floating", "floating-right"];

      return ["z-index-2"];
    },
    reportDetailsClass() {
      if (this.floatMode)
        return ["floating", "floating-right", "mt-4", "col-sm-3"];

      return ["z-index-2"];
    },
    wrapperClassPerMode() {
      if (this.loading) {
        return [];
      }
      if (!this.floatMode) {
        return ["d-flex"];
      }
      if (this.validationMode) {
        return ["d-flex", "flex-column"];
      }
      return [];
    },
    currentSelectedRawAnnotation() {
      if (!this.currentSelectedAnnotation) return null;

      return this.rawAnnotations.find(
        e => e.fhir_id === this.currentSelectedAnnotation.id
      );
    }
  },
  watch: {
    floatModeActive(val) {
      this.$emit("float-mode:changed", val);
    },
    internalWithAnnotationControls(val) {
      this.$emit("update:withAnnotationControls", val);
    },
    internalWithDiagnosisControls(val) {
      this.$emit("update:withDiagnosisControls", val);
    },
    internalWithAnnotationInstructions(val) {
      this.$emit("update:withAnnotationInstructions", val);
    },
    internalWithAnnotationDetails(val) {
      this.$emit("update:withAnnotationDetails", val);
    },
    internalWthAnnotations(val) {
      this.$emit("update:withAnnotations", val);
    },
    internalWithProjectDialog(val) {
      this.$emit("update:withProjectDialog", val);
    },
    internalAnnotationMode(val) {
      this.$emit("update:annotationMode", val);
    },
    internalWithSelectAnnotation(val) {
      this.$emit("update:withSelectAnnotation", val);
    }
  },
  methods: {
    async onRenderingSlide() {
      this.showMenu = true;
      // start preparing the map
      // add drawing layer
      if (!this.withDiagnosisControls) {
        this.map.addLayer(this.viewer.editLayer);
        console.log("on render", this.annotations);
        this.$nextTick(() => {
          this.annotations.forEach(e =>
            this.viewer.editLayer.getSource().addFeature(e)
          );
        });
      }
      this.removeLoading(3);

      if (this.withSelectAnnotation) {
        this.activateSelectControl();
      }

      if (this.validationMode) {
        // make sure validation control component is mounted and ready
        this.$nextTick(() => {
          this.$refs.validationControls.start();
        });
      }
    },
    activateSelectControl() {
      const componentClass = Vue.extend(ManipulationControl);
      const componentInstance = new componentClass({
        propsData: {
          tooltip: "Select",
          action: "select"
        }
      });
      componentInstance.$mount();
      const control = new SelectControl({
        element: componentInstance.$el,
        // target: this.$refs["select-control"],
        source: this.viewer.editLayer.getSource(),
        callback: this.selectAnnotation
      });
      control.setMap(this.map);
      componentInstance.control = control;
      componentInstance.$on("click", () => control.activate());
      componentInstance.click();
    },
    onAnnotationSelected(feature) {
      // if annotation is selected at validation times,
      // do not highlight but still show the annotation details
      this.currentSelectedAnnotation = this.observations.find(
        e => e.id === feature.get(FEATURE_OBSERVATION_ID)
      );
    },
    selectAnnotation(e) {
      // TODO refactor duplicated code with AnnotationControl.vue
      if (e.selected.length === 0) {
        this.currentSelectedAnnotation = null;
        return;
      }
      const observationId = e.selected[0].get(FEATURE_OBSERVATION_ID);
      this.currentSelectedAnnotation = this.observations.find(
        e => e.id === observationId
      );
    },
    onAnnotationCreated(e) {
      this.observations.push(e);
    },
    async onAnnotationStored(e) {
      console.log(e);
      const obs = await fetchResource(`Observation/${e.id}`);
      // replace
      const idx = this.observations.findIndex(x => x.id === e.uuid);
      this.observations[idx] = obs;
    },
    addLoading(status) {
      this.loadingStack.push(status);
    },
    removeLoading(amount) {
      if (!amount) amount = 1;
      for (let i = 0; i <= amount; i++) {
        this.loadingStack.pop();
      }
    },
    async loadAnnotations() {
      this.addLoading(this.statusLoadingAnnotations);
      // fetch observations
      let practitionerIdentifier = undefined;
      if (!this.validationMode) {
        practitionerIdentifier = this.tokenParsed["practitioner-identifier"];
      }
      this.rawAnnotations = await getAnnotationsForSlide(
        this.token,
        this.slide.id
      );
      this.observations = [];
      for (let cAnnotation of this.rawAnnotations) {
        console.log(cAnnotation);
        const observation = await fetchResource(
          `Observation/${cAnnotation.fhir_id}`
        );
        cAnnotation.observation = observation;
        this.observations.push(observation);
      }
      this.removeLoading();
      this.addLoading(this.statusPreparing);
      let annotations = this.rawAnnotations.map(e => {
        return observationToAnnotation(e);
      });
      annotations.forEach(e => {
        e.feature.setStyle(e.feature.get(FEATURE_STYLE));
        if (this.validationMode) {
          e.feature.set(FEATURE_STATUS, e.rawAnnotation.status);
        }
      });
      this.annotations = annotations.map(e => e.feature);
      this.removeLoading();
    },
    async loadImagingStudy() {
      this.addLoading(this.statusLoadingFHIR);
      let imagingStudy = this.findFhirResourceByIdentifier(
        this.slide.fhir_identifier
      );
      if (!imagingStudy) {
        imagingStudy = await fetchResourceWithIdentifier(
          "ImagingStudy",
          this.slide.fhir_identifier
        );
        this.addLoading(this.statusPreparing);
        imagingStudy = await prepareImagingStudy(imagingStudy);
        this.removeLoading();
      }
      // already load endpoint
      await this.$store.dispatch(LOAD, imagingStudy.endpointResource);
      this.imagingStudy = imagingStudy;
      this.removeLoading();
    },
    finalizeDiagnosis(status) {
      this.$emit('finalize', status);
    },
    goToSlide(slide) {
      this.$emit('go-to-slide', slide);
    },
    async initialize() {
      // this loading is for loading the slide
      this.addLoading(this.statusRendering);
      await this.loadImagingStudy();
      if (this.withAnnotations || this.withDiagnosisControls) {
        await this.loadAnnotations();
      }
      if (this.withProjectDialog) {
        this.dialogStart = true;
      }
      this.initialized = true;
    }
  },
  async mounted() {
    this.internalAnnotationMode = this.annotationMode;
    this.internalWithAnnotationControls = this.withAnnotationControls;
    this.internalWithDiagnosisControls = this.withDiagnosisControls;
    this.internalWithAnnotationInstructions = this.withAnnotationInstructions;
    this.internalWthAnnotations = this.withAnnotations;
    this.internalWithProjectDialog = this.withProjectDialog;
    this.internalAnnotationMode = this.annotationMode;
    this.internalWithSelectAnnotation = this.withSelectAnnotation;

    if (this.validationMode) {
      this.internalAnnotationMode = false;
      this.internalWithProjectDialog = false;
      this.internalWithAnnotationControls = false;
      this.internalWithDiagnosisControls = false;
      this.internalWithAnnotationInstructions = false;
      this.internalWithSelectAnnotation = true;
      this.internalWithAnnotationDetails = true;
      this.internalWithAnnotations = true;
    }

    await this.initialize();

    this.floatModeActive = this.floatMode;
  },
  components: {
    AnnotationControls,
    DiagnosisControls,
    AnnotationInstructions,
    AnnotationDetails,
    Visualization,
    ValidationControls
  }
};
</script>

<style lang="scss" scoped>
.floating {
  position: absolute;
  z-index: 1;
  top: 10px;
}

.z-index-2 {
  z-index: 2;
}

.floating-left {
  left: 10px;
}

.floating-right {
  right: 10px;
}

.wrapper {
  height: 100%;
  //border: 5px red solid;
}

.fullscreen {
  height: 100%;
  width: 100%;
}

.icon {
  width: 20px;
}

.loading {
  width: 100%;
}

.validation-controls {
  //position: absolute;
  //margin-left: auto;
  //margin-right: auto;
  //left: 0;
  //right: 0;
  //text-align: center;
}
</style>
